import React, { useState, useEffect, useMemo } from 'react';
import { Box, FormControl, InputLabel, Select, MenuItem, Typography, Paper, TextField, Button, Alert, Tabs, Tab, CircularProgress, Collapse, IconButton } from '@mui/material';
import { Info as InfoIcon } from '@mui/icons-material';
import QuestionForm from './QuestionForm';
import ExistingQuestions from './ExistingQuestions';
import { toast } from 'react-toastify';
import { httpsCallable } from 'firebase/functions';
import { functions, db } from '../firebase';
import StrategicPulse from './StrategicPulse';
import ClientProfile from './ClientProfile';
import IndustryInsightsSummaryCard from './IndustryInsightsSummaryCard';
import ChatSummaryModal from './ChatSummaryModal';
import ReportHistoryFeed from './ReportHistoryFeed';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';

const QuestionsSection = ({ clientId, clientIsActive, clients, user, userData, selectedClientId, onClientSelect, onQuestionSubmitted, userAsanaConnection, onNavigateToTab, onOpenWizard }) => {
const [currentContextClientId, setCurrentContextClientId] = useState(null);
const [selectedClientIsActive, setSelectedClientIsActive] = useState(true);
const [amsReportData, setAmsReportData] = useState(null);  
const [isAmsModalOpen, setIsAmsModalOpen] = useState(false);
  // State and handler for the AMS Report Generator
const [amsDateFrom, setAmsDateFrom] = useState('');
const [amsDateTo, setAmsDateTo] = useState('');
const [generatingReport, setGeneratingReport] = useState(false);
const [forwardedSearchQuery, setForwardedSearchQuery] = useState(null);
const [insightsSubTab, setInsightsSubTab] = useState(0);
const [showFeedFilterPanel, setShowFeedFilterPanel] = useState(false);
const [showChatSummaryInfo, setShowChatSummaryInfo] = useState(false);
const [feedFiltersActive, setFeedFiltersActive] = useState(false);
const [streamingQuestionId, setStreamingQuestionId] = useState(null);
const [streamingText, setStreamingText] = useState('');
const [streamingPhase, setStreamingPhase] = useState(null);
const AMS_MAX_DATE_RANGE_DAYS = 92;
// Prevent one-time-setup screen from flashing while clients are still loading
const [shouldShowOnboarding, setShouldShowOnboarding] = useState(false);

useEffect(() => {
  // If clients is not yet an array, we're still loading → never show onboarding
  if (!Array.isArray(clients)) {
    setShouldShowOnboarding(false);
    return;
  }

  // If clients are present, hide immediately (no debounce) to prevent flash
  if (clients.length > 0) {
    setShouldShowOnboarding(false);
    return;
  }

  // Only if confirmed empty after a short delay, show onboarding
  const timer = setTimeout(() => setShouldShowOnboarding(true), 350);
  return () => clearTimeout(timer);
}, [clients]);

const asanaContextForModal = useMemo(() => {
    const client = clients?.find(c => c.id === currentContextClientId);
    if (client?.asanaConnection?.status === 'connected') {
      return { type: 'client', id: client.id };
    }
    if (userAsanaConnection?.status === 'connected') {
      return { type: 'user' };
    }
    return null; // No connection available
}, [clients, currentContextClientId, userAsanaConnection]);

const dateRangeInfo = useMemo(() => {
    if (!amsDateFrom || !amsDateTo) return { isValid: true, days: 0 };
    const from = new Date(amsDateFrom);
    const to = new Date(amsDateTo);
    if (from > to) return { isValid: false, days: 0 }; // Already handled by disabled logic, but good practice
    
    const diffTime = Math.abs(to - from);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1; // +1 to be inclusive
    
    return {
        isValid: diffDays <= AMS_MAX_DATE_RANGE_DAYS,
        days: diffDays
    };
}, [amsDateFrom, amsDateTo]);

const handleInsightLinkClick = (insightId) => {
  toast.info(`Filtering feed for source: ${insightId.substring(0, 8)}...`);
  setForwardedSearchQuery({ id: insightId, timestamp: Date.now() });
  // Modal stays open - user can click multiple links or close manually
};

const handleGenerateAms = async () => {
  // Validate dates
  if (new Date(amsDateFrom) > new Date(amsDateTo)) {
    toast.error('Start date must be before end date');
    return;
  }
  
  setGeneratingReport(true);
  setIsAmsModalOpen(true); // Open modal immediately (shows loading state)
  setAmsReportData(null); // Clear previous data
  
  try {
    const generateAMS = httpsCallable(functions, 'generateArchiveMasterSummary', {
      timeout: 450000  // 7.5 minutes in milliseconds
    });
    const result = await generateAMS({
      clientId: currentContextClientId,
      dateFrom: amsDateFrom,
      dateTo: amsDateTo
    });
    
    setAmsReportData(result.data); // Set the report data for modal

    // --- Phase 5: Persist Chat Summary to clientReports ---
    try {
      const reportContent = result.data.reportText || '';
      const plainPreview = reportContent
        .replace(/#{1,6}\s/g, '')
        .replace(/\*\*([^*]+)\*\*/g, '$1')
        .replace(/\*([^*]+)\*/g, '$1')
        .replace(/\n+/g, ' ')
        .trim()
        .substring(0, 200);

      await addDoc(collection(db, 'clientReports'), {
        clientId: currentContextClientId,
        fcfoId: user.uid,
        reportType: 'chat_summary',
        title: `Chat Summary: ${amsDateFrom} — ${amsDateTo}`,
        content: reportContent,
        contentPreview: plainPreview,
        dateFrom: amsDateFrom,
        dateTo: amsDateTo,
        generatedAt: serverTimestamp(),
        generatedBy: user.uid,
        organizationId: userData?.organizationId || null,
        provider: result.data.provider || 'Claude Sonnet 4.5',
        topicTags: []
      });
    } catch (persistError) {
      console.error('[Phase5] Failed to persist Chat Summary report:', persistError);
      // Non-blocking — modal still shows even if persistence fails
    }
    // --- End Phase 5 ---

    // DO NOT reset dates. Keep them in sync with the generated report.
  
  } catch (error) {
    console.error('Error generating Chat Summary report:', error);
    toast.error(error.message || 'Failed to generate report');
    setIsAmsModalOpen(false); // Close modal on error
  } finally {
    setGeneratingReport(false);
  }
};

    const handleStreamStart = (questionId) => {
    setStreamingQuestionId(questionId);
    setStreamingText('');
    setStreamingPhase('assembling_context');
  };

  const handleStreamChunk = (text) => {
    setStreamingText(prev => prev + text);
  };

  const handleStreamPhase = (phase) => {
    setStreamingPhase(phase);
  };

  const handleStreamDone = () => {
    // Keep streaming state in place until Firestore snapshot includes the final answer.
    setStreamingPhase('done');
  };

  const handleStreamError = () => {
    setStreamingQuestionId(null);
    setStreamingText('');
    setStreamingPhase(null);
  };

  const handleStreamingQuestionPersisted = (questionId) => {
    setStreamingQuestionId(current => current === questionId ? null : current);
    setStreamingText('');
    setStreamingPhase(null);
  };

  console.log('🔍 QuestionsSection rendering with props:', {
    clientId,
    clientIsActive,
    clients: clients?.length || 0,
    user: user?.email || 'no user'
  });

  // Set default client when clients load
  useEffect(() => {
    console.log('[DEBUG] QuestionsSection useEffect - clients changed:', {
      clientsLength: clients?.length,
      currentContextClientId,
      clientId
    });
    if (!Array.isArray(clients)) return;
    if (clients && clients.length > 0 && !currentContextClientId) {
      // If we have a specific clientId prop, use it
      if (clientId) {
        console.log('[DEBUG] Using provided clientId:', clientId);
        setCurrentContextClientId(clientId);
        const client = clients.find(c => c.id === clientId);
        setSelectedClientIsActive(client?.isActive === true);


      } else {
        // Default to first client
        const firstClient = clients[0];
        console.log('[DEBUG] Setting default client to first client:', firstClient.id);
        setCurrentContextClientId(firstClient.id);
        setSelectedClientIsActive(firstClient.isActive === true);
      }
    }


    // Auto-select the client if there is only one
    if (clients && clients.length === 1) {
      const singleClient = clients[0];
      if (currentContextClientId !== singleClient.id) {
        console.log('[DEBUG] Auto-selecting the only client:', singleClient.id);
        setCurrentContextClientId(singleClient.id);
        setSelectedClientIsActive(singleClient.isActive === true);
      }
    }
  }, [clients, clientId]); // ✅ FIXED: Removed currentContextClientId

  // Re-derive selectedClientIsActive when client data updates (e.g. after wizard activation)
useEffect(() => {
    if (!currentContextClientId || !Array.isArray(clients)) return;
    if (currentContextClientId === 'ALL') {
        setSelectedClientIsActive(true);
        return;
    }
    const client = clients.find(c => c.id === currentContextClientId);
    if (client) {
        setSelectedClientIsActive(client.isActive === true);
    }
}, [clients, currentContextClientId]);

 // Handle client selection change
const handleClientChange = (event) => {
    const newClientId = event.target.value;
    console.log('[DEBUG] Client selection changed to:', newClientId);
    setCurrentContextClientId(newClientId);

    // --- FIX: Reset Chat Summary state when client changes ---
    setAmsReportData(null);
    setAmsDateFrom('');
    setAmsDateTo('');
    // --- END FIX ---

    if (newClientId === 'ALL') {
        setSelectedClientIsActive(true);
    } else {
        const selectedClient = clients?.find(c => c.id === newClientId);
        setSelectedClientIsActive(selectedClient?.isActive === true);
    }
};

  // If no clients loaded yet, show the new user setup instructions (debounced to avoid flash)
if (shouldShowOnboarding) {
    return (
        <Box p={3} sx={{ maxWidth: '600px', margin: 'auto', textAlign: 'center', mt: 4 }}>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold' }}>
                Welcome to SavvaiEdge!
            </Typography>
            <Typography variant="body1" sx={{ mb: 4, color: 'text.secondary' }}>
                Let's set up your first client. This guided 3-step process takes just a few minutes — you'll provide company details, connect financial data, and answer a few strategic questions to unlock AI-powered insights.
            </Typography>

            <Button
                variant="contained"
                color="primary"
                size="large"
                onClick={() => onOpenWizard(null)}
                sx={{ textTransform: 'none', px: 4, py: 1.5, fontSize: '1.1rem', mb: 4 }}
            >
                Set Up Your First Client
            </Button>

            <Box sx={{ textAlign: 'left' }}>
                <Typography
                    variant="body2"
                    sx={{
                        cursor: 'pointer',
                        color: 'text.secondary',
                        display: 'inline-flex',
                        alignItems: 'center',
                        '&:hover': { color: 'primary.main' }
                    }}
                    onClick={(e) => {
                        const details = e.currentTarget.nextElementSibling;
                        if (details) details.style.display = details.style.display === 'none' ? 'block' : 'none';
                    }}
                >
                    ▸ How does SavvaiEdge protect privacy?
                </Typography>
                <Paper elevation={1} sx={{ p: 2, mt: 1, backgroundColor: 'grey.50', display: 'none' }}>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                        Our AI doesn't need to know your client's name to understand their business. Before any data is sent to AI models, we anonymize it by replacing identifying information with generic placeholders. The AI analyzes financial patterns, market trends, and strategic goals without ever knowing the identity behind the data. We then re-insert your client's name only within your private, secure portal.
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Think of it like a doctor reading an X-ray — they can make a precise diagnosis without knowing the patient's name. Our AI works the same way with your business data.
                    </Typography>
                </Paper>
            </Box>
        </Box>
    );
}

 // Guard: while clients are loading (not an array yet), render nothing to avoid flash/errors
  if (!Array.isArray(clients)) {
    return null;
  }

  console.log('[DEBUG] Rendering QuestionsSection with currentContextClientId:', currentContextClientId);

  // Trial status calculations
  const isTrialing = userData?.stripeSubscriptionStatus === 'trialing';
  const trialActivated = userData?.trialActivated === true;
  const trialEnd = userData?.trialEnd;

  const getDateFromField = (field) => {
    if (!field) return null;
    if (typeof field.toDate === 'function') return field.toDate();
    if (field instanceof Date) return field;
    return new Date(field);
  };

  const trialDaysRemaining = (() => {
    const endDate = getDateFromField(trialEnd);
    if (!endDate) return null;
    const now = new Date();
    const diffTime = endDate - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 0;
  })();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      {/* Trial Status Banner */}
      {isTrialing && !trialActivated && (
        <Alert 
          severity="info" 
          sx={{ mb: 2 }}
          action={
            <Button 
              color="inherit" 
              size="small" 
              onClick={() => onNavigateToTab(2)}
            >
              Activate Now
            </Button>
          }
        >
          {trialDaysRemaining !== null ? (
            <>🎉 <strong>{trialDaysRemaining} day{trialDaysRemaining !== 1 ? 's' : ''}</strong> left in your free trial.</>
          ) : (
            <>🎉 You're on a free trial.</>
          )}
          {' '}Activate your subscription to ensure uninterrupted access.
        </Alert>
      )}

      {/* Trial Activated Confirmation */}
      {isTrialing && trialActivated && (
        <Alert severity="success" sx={{ mb: 2 }}>
          ✅ Subscription activated! Your trial continues and you'll be charged automatically when it ends.
        </Alert>
      )}

      {/* Insights Subtabs + Client Selector on same row */}
                  <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 1, display: 'flex', alignItems: 'center', gap: 2, pb: 0.5 }}>
        <Tabs
          value={insightsSubTab}
          onChange={(e, newValue) => setInsightsSubTab(newValue)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            minHeight: 40,
            flex: 1,
            '& .MuiTab-root': {
              minHeight: 40,
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '0.875rem',
            },
          }}
        >
          <Tab label="Feed" />
          <Tab label="Strategic Pulse" />
          <Tab label="Chat Summary" />
          <Tab label="Client Profile" />
          <Tab label="Industry Insights" />
        </Tabs>
        {clients.length > 1 ? (
            <FormControl size="small" sx={{ minWidth: 220, flexShrink: 0 }}>
            <Select
              value={currentContextClientId || ''}
              onChange={handleClientChange}
              displayEmpty
            >
              <MenuItem value="" disabled>Select Client</MenuItem>
              <MenuItem value="ALL">All Clients</MenuItem>
              {clients.map((client) => (
                <MenuItem key={client.id} value={client.id}>
                  {client.displayName || client.name || 'Unnamed Client'}
                  {client.isActive === false && ' (Inactive)'}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : (
          <Typography variant="body2" sx={{ flexShrink: 0, pr: 1, fontWeight: 500 }}>
            {clients[0]?.displayName || clients[0]?.name || 'Unnamed Client'}
          </Typography>
        )}
      </Box>

      {/* ===== TAB 0: FEED ===== */}
      {insightsSubTab === 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 220px)', minHeight: 400 }}>
        {/* Feed area — scrollable, takes available space */}
        <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0 }} id="feed-scroll-container">
          {currentContextClientId ? (
          <ExistingQuestions
              clientId={currentContextClientId}
              clientIsActive={selectedClientIsActive}
              clients={clients}
              user={user}
              userAsanaConnection={userAsanaConnection}
              onQuestionUpdate={onQuestionSubmitted}
              forwardedSearchQuery={forwardedSearchQuery}
              showFilterPanel={showFeedFilterPanel}
              onToggleFilterPanel={() => setShowFeedFilterPanel(prev => !prev)}
              onFilterActiveChange={setFeedFiltersActive}
              streamingQuestionId={streamingQuestionId}
              streamingText={streamingText}
              streamingPhase={streamingPhase}
              onStreamingQuestionPersisted={handleStreamingQuestionPersisted}
            />
          ) : (
            <Box p={3}>
              <Typography>Please select a client to view questions</Typography>
            </Box>
          )}
        </Box>
        {/* Input bar — pinned at bottom */}
                <Box sx={{ flexShrink: 0, borderTop: '1px solid', borderColor: 'divider', pt: 1.5, mt: 1, pb: 2 }}>
                    <QuestionForm
            clientId={currentContextClientId}
            clientIsActive={selectedClientIsActive}
            clients={clients}
            user={user}
            onQuestionSubmitted={onQuestionSubmitted}
            compact
            onToggleFilterPanel={() => setShowFeedFilterPanel(prev => !prev)}
            filtersActive={feedFiltersActive}
            onStreamStart={handleStreamStart}
            onStreamChunk={handleStreamChunk}
            onStreamPhase={handleStreamPhase}
            onStreamDone={handleStreamDone}
            onStreamError={handleStreamError}
          />
        </Box>
      </Box>
      )}

      {/* ===== TAB 1: STRATEGIC PULSE ===== */}
      {insightsSubTab === 1 && (
        <Box>
          <StrategicPulse
            clientId={currentContextClientId}
            currentNarrative={
              clients?.find(c => c.id === currentContextClientId)?.clientStrategicNarrative
            }
            onUpdate={() => {
              console.log('[DEBUG] Strategic narrative updated.');
            }}
            clientIsActive={selectedClientIsActive}
            user={user}
            userData={userData}
          />
          <ReportHistoryFeed
            reportType="strategic_pulse"
            clientId={currentContextClientId}
            fcfoId={user?.uid}
          />
        </Box>
      )}

      {/* ===== TAB 2: CHAT SUMMARY ===== */}
      {insightsSubTab === 2 && (
        <Box>
          <Box display="flex" alignItems="center" gap={1} sx={{ mb: 1 }}>
            <Typography variant="h6" fontWeight={600}>Chat Summary</Typography>
            <IconButton size="small" onClick={() => setShowChatSummaryInfo(prev => !prev)}>
              <InfoIcon fontSize="small" />
            </IconButton>
          </Box>
          <Collapse in={showChatSummaryInfo}>
            <Alert severity="info" sx={{ mb: 2 }} onClose={() => setShowChatSummaryInfo(false)}>
              <Typography variant="body2" sx={{ mb: 1 }}><strong>What this generates:</strong> A comprehensive narrative report of all Q&A activity for the selected client and date range, including key themes, insights, hyperlinks to source questions, and a summary of strategic topics discussed.</Typography>
              <Typography variant="body2" sx={{ mb: 1 }}><strong>How to use it:</strong> Select a date range, click Generate, and the report opens automatically. Use it to prepare for client meetings, document advisory work, or share a summary of AI-assisted analysis with a colleague or client.</Typography>
              <Typography variant="body2"><strong>Date range tip:</strong> Reports covering more than 90 days may take 2-3 minutes to generate. For large date ranges, generating by quarter gives the most focused output.</Typography>
            </Alert>
          </Collapse>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Comprehensive history report with insights and hyperlinks.
          </Typography>

          <Box sx={{ display: 'flex', gap: 1.5, mb: 2, alignItems: 'center' }}>
            <TextField
              label="Date From"
              type="date"
              value={amsDateFrom}
              onChange={(e) => setAmsDateFrom(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
              disabled={generatingReport}
              sx={{ maxWidth: 180 }}
            />
            <TextField
              label="Date To"
              type="date"
              value={amsDateTo}
              onChange={(e) => setAmsDateTo(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
              disabled={generatingReport}
              sx={{ maxWidth: 180 }}
            />
            <Button
              variant="contained"
              color="secondary"
              onClick={handleGenerateAms}
              disabled={
                generatingReport ||
                !currentContextClientId ||
                currentContextClientId === 'ALL' ||
                !amsDateFrom ||
                !amsDateTo ||
                (amsDateFrom && amsDateTo && new Date(amsDateFrom) > new Date(amsDateTo)) ||
                !dateRangeInfo.isValid
              }
              sx={{ fontWeight: 600, whiteSpace: 'nowrap', minWidth: 130, px: 3 }}
            >
              {generatingReport ? 'Generating…' : 'Generate'}
            </Button>
          </Box>

          {amsDateFrom && amsDateTo && new Date(amsDateFrom) > new Date(amsDateTo) && (
            <Typography variant="caption" color="error" sx={{ mb: 2, display: 'block' }}>
              Start date must be before end date
            </Typography>
          )}
          {amsDateFrom && amsDateTo && !(new Date(amsDateFrom) > new Date(amsDateTo)) && !dateRangeInfo.isValid && (
            <Typography variant="caption" color="error" sx={{ mb: 2, display: 'block' }}>
              {`Date range cannot exceed ${AMS_MAX_DATE_RANGE_DAYS} days. Current range is ${dateRangeInfo.days} days.`}
            </Typography>
          )}

          {/* "View Last Report" button when a report exists but modal is closed */}
          {amsReportData && !generatingReport && !isAmsModalOpen && (
            <Button
              variant="text"
              size="small"
              onClick={() => setIsAmsModalOpen(true)}
              sx={{ mt: 1 }}
            >
              View Last Report
            </Button>
          )}

          <ReportHistoryFeed
            reportType="chat_summary"
            clientId={currentContextClientId}
            fcfoId={user?.uid}
          />
        </Box>
      )}

      {/* ===== TAB 3: CLIENT PROFILE ===== */}
      {insightsSubTab === 3 && (
        <Box>
          <ClientProfile
          clientId={currentContextClientId}
          clientIsActive={selectedClientIsActive}
          user={user}
          userData={userData}
        />
          <ReportHistoryFeed
            reportType="client_profile"
            clientId={currentContextClientId}
            fcfoId={user?.uid}
          />
        </Box>
      )}

      {/* ===== TAB 4: INDUSTRY INSIGHTS ===== */}
      {insightsSubTab === 4 && (
        <Box>
          <IndustryInsightsSummaryCard userData={userData} />
          <ReportHistoryFeed
            reportType="industry_insights"
            clientId={currentContextClientId}
            fcfoId={user?.uid}
          />
        </Box>
      )}
      
      {/* Chat Summary Modal */}
      <ChatSummaryModal
        open={isAmsModalOpen}
        onClose={() => setIsAmsModalOpen(false)}
        reportData={amsReportData}
        isLoading={generatingReport}
        user={user}
        asanaContext={asanaContextForModal}
        clientId={currentContextClientId}
      />
    </Box>
  );
};

export default QuestionsSection;